/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import socket from '../lib/socket';
import api from '../services/api';

type NotificationData = {
  name: string;
  city: string;
  plan: string;
  timeAgo: string;
};

type UISettings = {
  notificationIntervalMinutes?: number;
  firstNotificationDelaySeconds?: number;
  notificationsEnabled?: boolean;
};

const TEST_MODE = false;

const getRandomDelay = (intervalMinutes: number = 5) => {
  if (TEST_MODE) return 5000;
  // Usa o intervalo configurado com variação de ±20%
  const baseInterval = intervalMinutes * 60 * 1000;
  const min = baseInterval * 0.8;
  const max = baseInterval * 1.2;
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export function NotificationManager() {
  const queue = useRef<NotificationData[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uiSettings, setUiSettings] = useState<UISettings | null>(null);
  const isFirstNotification = useRef(true);

  // Carrega as configurações de UI
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get<UISettings>('/ui-settings');
        setUiSettings(response.data);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    // Se as notificações estiverem desabilitadas, não faz nada
    if (uiSettings?.notificationsEnabled === false) {
      return;
    }

    socket.on('new-notification', (data: NotificationData) => {
      queue.current.push(data);
      if (!timer.current) scheduleNextToast();
    });

    return () => {
      socket.off('new-notification');
      if (timer.current) clearTimeout(timer.current);
    };
  }, [uiSettings]);

  const scheduleNextToast = () => {
    // Determina o delay baseado nas configurações
    let nextDelay: number;

    if (isFirstNotification.current && uiSettings?.firstNotificationDelaySeconds) {
      // Primeira notificação usa o delay configurado
      nextDelay = TEST_MODE ? 3000 : uiSettings.firstNotificationDelaySeconds * 1000;
      isFirstNotification.current = false;
    } else {
      // Notificações subsequentes usam o intervalo configurado
      nextDelay = getRandomDelay(uiSettings?.notificationIntervalMinutes);
    }

    timer.current = setTimeout(() => {
      const next = queue.current.shift();
      if (next) {
        toast.custom((t) => (
          <div
            className={`${
              t.visible ? 'animate-enter' : 'animate-leave'
            } max-w-md w-full bg-[#f4f9fe] shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black/10 ring-opacity-5 p-4`}
          >
            <div className="flex-1 w-0">
              <p className="text-sm font-medium text-gray-900">
                {next.name}, de {next.city}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Assinou o <span className="text-[#0083cc] font-bold">{next.plan}</span> {next.timeAgo}.
              </p>
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Fechar</span>✖
              </button>
            </div>
          </div>
        ));
      }

      if (queue.current.length > 0) {
        scheduleNextToast();
      } else {
        timer.current = null;
        // Reseta a flag para a próxima sessão
        isFirstNotification.current = true;
      }
    }, nextDelay);
  };

  return null;
}
